import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@bymason-private/auth-provider';
import {
  Box,
  Button,
  ControllerModal,
  DialogActions,
  DialogContent,
  TextField,
  Stack,
  Typography,
  CircularProgress,
} from '@bymason-private/legacy-pattern-components';
import { ApiKeysModel, useCreateApiKey, useGetApiScopes } from '@bymason/controller-api-hooks';
import { ScopeTree } from '../../../';
import { ApiKeyCreationSuccessModal } from './api-key-creation-success-modal';

interface CreateApiKeyModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (apiKey: ApiKeysModel) => void;
  onError?: (message: string, code?: number) => void;
}

export function CreateApiKeyModal({ onClose, open, onSuccess, onError }: CreateApiKeyModalProps) {
  const { authToken = '' } = useAuth();
  const [createApiKey, apiKey, createApiKeyError, createApiKeyBusy] = useCreateApiKey(authToken);
  const [getScopes, scopes, scopesError, scopesBusy] = useGetApiScopes(authToken);
  const [checked, setChecked] = useState<string[]>([]);
  const [name, setName] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const enableCreate = useMemo(() => name.trim() && checked.length > 0, [name, checked]);

  const handleSubmit = useCallback(() => {
    createApiKey({ desc: name, scopes: checked });
  }, [checked, createApiKey, name]);

  const handleNameChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setName(evt.target.value);
  };

  const resetFormState = () => {
    setName('');
    setChecked([]);
  };

  useEffect(() => {
    if (apiKey) {
      setShowSuccessModal(true);
      onSuccess && onSuccess(apiKey);
    }
  }, [apiKey, onSuccess]);

  useEffect(() => {
    if (!createApiKeyBusy && createApiKeyError) {
      onError && onError(createApiKeyError);
    }
  }, [createApiKeyBusy, createApiKeyError, onError]);

  useEffect(() => {
    if (!scopesBusy && scopesError) {
      onError && onError(scopesError);
    }
  }, [scopesBusy, scopesError, onError]);

  useEffect(() => {
    if (open) {
      resetFormState();
    }
  }, [open]);

  useEffect(() => {
    getScopes({});
  }, [getScopes]);

  return (
    <ControllerModal onClose={onClose} open={open} title='Create new API key'>
      <DialogContent>
        <Stack py={2} direction='column' spacing={3}>
          <Box sx={{ mb: 1, display: 'flex' }}>
            <TextField label='Name' value={name} autoFocus onChange={handleNameChange} required fullWidth />
          </Box>
          <hr />
          <Box sx={{ mt: 3 }}>
            <Typography fontWeight={700}>Scopes</Typography>
            {scopesBusy ? (
              <Box display='flex' columnGap={2}>
                <span>Loading scopes</span>
                <CircularProgress size={20} />
              </Box>
            ) : (
              <ScopeTree checked={checked} setChecked={setChecked} scopes={scopes} userScopes={[]} />
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color='secondary' onClick={onClose} size='medium' variant='text'>
          Cancel
        </Button>
        <Button
          color='primary'
          size='medium'
          variant='contained'
          onClick={handleSubmit}
          disabled={!enableCreate}
        >
          Create key
        </Button>
      </DialogActions>
      {showSuccessModal && apiKey && (
        <ApiKeyCreationSuccessModal apiKey={apiKey} onClose={onClose} open={showSuccessModal} />
      )}
    </ControllerModal>
  );
}
